import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Button from "../../../components/staff/Button";
import CheckBox from "../../../components/staff/CheckBox";
import Close from "../../../components/Atomos/Icons/Close";
import Mail from "../../../components/Atomos/Icons/Mail";
import Modal from "../../../components/Atomos/Modals/Modal";
import {
  getProfessionalAccounts,
  MAX_PROVISION_BATCH,
  provisionProfessionalAccounts,
  resendProfessionalCredentials,
  type CredentialEmailResult,
  type ProfessionalAccountStatus,
} from "../../../Services/IntegratorFirm";
import { toastError, toastSuccess, toastWarn } from "../../../utils/toast";
import type { ReviewPersonRow } from "./resultSummaryModel";
import { TABLE_CELL, TABLE_HEAD, TABLE_ROW } from "./staffSurface";

/** Backend cap on one `professionalAccounts` request. */
const STATUS_CHUNK = 500;
const MODAL_BUTTON_CLASS = "w-full justify-center sm:w-[12.5rem]";

interface ProfessionalAdditionsProps {
  firmId: string;
  rows: ReviewPersonRow[];
}

/**
 * The professionals a run added, with their B2C account state. Staff select the ones
 * ready for a login and create their accounts in one all-or-nothing request; those that
 * already have an account can get their credentials email again.
 */
export default function ProfessionalAdditions({
  firmId,
  rows,
}: ProfessionalAdditionsProps): ReactElement {
  const personIds = useMemo(
    () => rows.map((row) => row.personId).filter((id): id is string => Boolean(id)),
    [rows]
  );

  const [statuses, setStatuses] = useState<Map<string, ProfessionalAccountStatus>>(
    new Map()
  );
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const loadStatuses = useCallback(async () => {
    if (!firmId || personIds.length === 0) {
      return;
    }
    setLoadingStatuses(true);
    setStatusError("");
    try {
      const next = new Map<string, ProfessionalAccountStatus>();
      for (let start = 0; start < personIds.length; start += STATUS_CHUNK) {
        const chunk = await getProfessionalAccounts({
          firmId,
          personIds: personIds.slice(start, start + STATUS_CHUNK),
        });
        for (const status of chunk) {
          next.set(status.personId, status);
        }
      }
      setStatuses(next);
      setSelected((current) => {
        const kept = new Set<string>();
        current.forEach((id) => {
          if (next.get(id)?.eligible) {
            kept.add(id);
          }
        });
        return kept;
      });
    } catch (error: any) {
      setStatusError(error?.message ?? "Could not load account status.");
    } finally {
      setLoadingStatuses(false);
    }
  }, [firmId, personIds]);

  useEffect(() => {
    void loadStatuses();
  }, [loadStatuses]);

  const eligibleIds = useMemo(
    () => personIds.filter((id) => statuses.get(id)?.eligible),
    [personIds, statuses]
  );
  const allEligibleSelected =
    eligibleIds.length > 0 && eligibleIds.every((id) => selected.has(id));
  const overCap = selected.size > MAX_PROVISION_BATCH;
  const isActing = isProvisioning || resendingId != null;

  const toggleOne = (personId: string, checked: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(personId);
      }
      else {
        next.delete(personId);
      }
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelected(checked ? new Set(eligibleIds) : new Set());
  };

  const handleProvision = async () => {
    if (selected.size === 0 || overCap || isActing) {
      return;
    }
    setIsProvisioning(true);
    try {
      const results = await provisionProfessionalAccounts({
        firmId,
        personIds: Array.from(selected),
      });
      reportEmailResults(results, "created");
      setSelected(new Set());
    } catch (error: any) {
      toastError(error?.message ?? "No accounts were created.", { autoClose: 10000 });
    } finally {
      setIsProvisioning(false);
      setConfirmOpen(false);
      await loadStatuses();
    }
  };

  const handleResend = async (personId: string) => {
    if (isActing) {
      return;
    }
    setResendingId(personId);
    try {
      const results = await resendProfessionalCredentials({
        firmId,
        personIds: [personId],
      });
      reportEmailResults(results, "resent");
    } catch (error: any) {
      toastError(error?.message ?? "Could not resend the credentials.", {
        autoClose: 6000,
      });
    } finally {
      setResendingId(null);
    }
  };

  if (rows.length === 0) {
    return <p className="text-sm italic text-gray-500">No professionals were added.</p>;
  }

  return (
    <>
      <Modal
        isModalOpen={confirmOpen}
        toggleModal={() => !isProvisioning && setConfirmOpen(false)}
        title="Create users and send passwords"
        titleColor="text-secondary font-semibold text-lg"
        compact
        outerPaddingOnly
        compactBodyScrolls={false}
        className="w-full p-10 !max-w-md !overflow-visible border border-gray-200 shadow-lg sm:!max-w-lg"
        bodyClassName="!pt-0"
      >
        <div className="flex flex-col gap-4">
          <p className="text-left text-sm leading-relaxed text-[#474F56] sm:text-base">
            Create {selected.size} B2C{" "}
            {selected.size === 1 ? "account" : "accounts"} and email each
            professional their login instructions? If any account cannot be
            created, none of them are.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
            <Button
              className={MODAL_BUTTON_CLASS}
              text="No, go back"
              onClick={() => !isProvisioning && setConfirmOpen(false)}
              color="neutral"
              variant="outlined"
              size="fixed"
              icon={<Close />}
              disabled={isProvisioning}
            />
            <Button
              className={MODAL_BUTTON_CLASS}
              text={isProvisioning ? "Creating…" : "Yes, create"}
              onClick={() => void handleProvision()}
              color="secondary"
              variant="filled"
              size="fixed"
              disabled={isProvisioning}
            />
          </div>
        </div>
      </Modal>

      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-gray-500">
          {loadingStatuses
            ? "Checking accounts…"
            : `${eligibleIds.length} ready for an account · ${selected.size} selected`}
          {overCap && (
            <span className="ml-2 text-red-700">
              Select at most {MAX_PROVISION_BATCH} at a time.
            </span>
          )}
        </p>
        <Button
          className="w-full justify-center sm:w-auto"
          text={isProvisioning ? "Creating…" : "Create users and send passwords"}
          color="secondary"
          variant="filled"
          size="fixed"
          icon={<Mail />}
          disabled={selected.size === 0 || overCap || isActing || loadingStatuses}
          tooltip="Create a B2C account for each selected professional and email their login instructions"
          onClick={() => setConfirmOpen(true)}
        />
      </div>

      {statusError && (
        <p className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {statusError}
        </p>
      )}

      <div className="max-h-80 overflow-auto">
        <table className="w-full border-collapse text-sm text-gray-font">
          <thead>
            <tr>
              <th className={`${TABLE_HEAD} w-10`}>
                <CheckBox
                  checked={allEligibleSelected}
                  disabled={eligibleIds.length === 0 || isActing}
                  tooltip="Select all eligible"
                  onCheckedChange={toggleAll}
                />
              </th>
              <th className={TABLE_HEAD}>Professional</th>
              <th className={TABLE_HEAD}>Email</th>
              <th className={TABLE_HEAD}>Account</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const status = row.personId ? statuses.get(row.personId) : undefined;
              return (
                <tr key={`${row.email}-${row.name}-${index}`} className={TABLE_ROW}>
                  <td className={TABLE_CELL}>
                    <CheckBox
                      checked={Boolean(row.personId && selected.has(row.personId))}
                      disabled={!status?.eligible || isActing}
                      onCheckedChange={(checked) =>
                        row.personId && toggleOne(row.personId, checked)
                      }
                    />
                  </td>
                  <td className={TABLE_CELL}>
                    {row.personId ? (
                      <Link
                        to={`/staff/profile/attorney/${row.personId}`}
                        className="text-secondary"
                      >
                        {row.name || "—"}
                      </Link>
                    ) : (
                      row.name || "—"
                    )}
                  </td>
                  <td className={TABLE_CELL}>{row.email || "—"}</td>
                  <td className={TABLE_CELL}>
                    <AccountCell
                      status={status}
                      loading={loadingStatuses && !status}
                      resending={resendingId === row.personId}
                      disabled={isActing}
                      onResend={() => row.personId && void handleResend(row.personId)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function AccountCell({
  status,
  loading,
  resending,
  disabled,
  onResend,
}: {
  status?: ProfessionalAccountStatus;
  loading: boolean;
  resending: boolean;
  disabled: boolean;
  onResend: () => void;
}): ReactElement {
  if (loading) {
    return <span className="text-gray-500">Checking…</span>;
  }
  if (!status) {
    return <span className="text-gray-500">—</span>;
  }

  if (status.hasAccount) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-green-700">
          Account created{status.userName ? ` (${status.userName})` : ""}
        </span>
        <button
          type="button"
          className="text-xs font-medium text-secondary underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={disabled}
          onClick={onResend}
        >
          {resending ? "Sending…" : "Resend email"}
        </button>
      </div>
    );
  }

  if (status.eligible) {
    return <span>Ready</span>;
  }
  return <span className="text-red-700">{status.reasons.join(" ") || "Not eligible."}</span>;
}

function reportEmailResults(
  results: CredentialEmailResult[],
  verb: "created" | "resent"
): void {
  const failed = results.filter((result) => !result.emailSent);
  const sent = results.length - failed.length;

  if (verb === "created") {
    toastSuccess(
      `${results.length} ${results.length === 1 ? "account" : "accounts"} created, ` +
        `${sent} ${sent === 1 ? "email" : "emails"} sent.`
    );
  } else if (sent > 0) {
    toastSuccess(`Credentials email sent to ${results[0]?.email || "the professional"}.`);
  }

  if (failed.length > 0) {
    const names = failed
      .map((result) => `${result.name || result.email}: ${result.emailError ?? "unknown error"}`)
      .join("; ");
    toastWarn(
      `The account exists but the email could not be sent to ${names}. Use "Resend email" to try again.`,
      { autoClose: 10000 }
    );
  }
}
